/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import InputField from "components/fields/InputField";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";

const InputStyle =
  "daterange-picker font-medium text-navy-700 h-10 mt-0 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: { variant: "outline" },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)",
      borderRadius: "2px",
      _hover: { background: "gray", color: "white" },
    },
    isInRangeBtnProps: { background: "gray", color: "white" },
    selectedBtnProps: { background: "black", color: "white" },
    todayBtnProps: {},
  },
  inputProps: { className: InputStyle },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  calendarPanelProps: {
    wrapperProps: { borderColor: "#81E6D9" },
    contentProps: { borderWidth: 0 },
    headerProps: { padding: "5px" },
    dividerProps: { display: "none" },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

type Props = {
  showFilters: boolean;
  userRole: any;
  isFilterLoading: boolean;
  filters: any;
  setFilters: (v: any) => void;
  setSelectedDates: (v: any) => void;
  selectedDates: any;
  handleSearchSubmit: () => void;
  handleSearchClear: () => void;
  assetList: Array<{ name: string }> | null;
};

const TradeSearch: React.FC<Props> = ({
  showFilters,
  userRole,
  isFilterLoading,
  filters,
  setFilters,
  setSelectedDates,
  selectedDates,
  handleSearchSubmit,
  handleSearchClear,
  assetList,
}) => {
  // Modal states for merchant and subaccount selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);

  const handleInputChange = (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterTrades", merchantData.id);
    localStorage.setItem("subaccountIdToFilterTrades", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterTrades", "");
    localStorage.setItem("subaccountIdToFilterTrades", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    localStorage.setItem("subaccountIdToFilterTrades", subaccountData.id);
    setFilters({ ...filters, subaccount_id: subaccountData.id });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    localStorage.setItem("subaccountIdToFilterTrades", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  const handleClear = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterTrades", "");
    localStorage.setItem("subaccountIdToFilterTrades", "");
    handleSearchClear();
  };

  // If not showing filters, render nothing (button lives outside in the toolbar)
  if (!showFilters) return null;

  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Date Range
          </label>
          <div className="search-picker-custom text-sm">
            <RangeDatepicker
              disabled={isFilterLoading}
              selectedDates={selectedDates}
              onDateChange={setSelectedDates}
              propsConfigs={dateRangePropsConfigs}
              configs={{ dateFormat: "MMM d, yyyy" }}
              maxDate={new Date()}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Status
          </label>
          <Select
            isDisabled={isFilterLoading}
            id="status"
            value={filters.status}
            onChange={handleInputChange}
            className="relative !h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="0">Awaiting</option>
            <option value="1">Pending</option>
            <option value="2">Completed</option>
            <option value="3">Cancelled</option>
            <option value="4">Rejected</option>
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Trade Code
          </label>
          <InputField
            disabled={isFilterLoading}
            variant="auth"
            extra="shadow-sm"
            label="Trade Code"
            hideLabel
            placeholder="e.g. 5Md22K6yw8"
            id="shortid"
            type="text"
            autoComplete="off"
            value={filters.shortid}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Asset
          </label>
          <Select
            isDisabled={isFilterLoading}
            id="asset"
            value={filters.asset}
            onChange={handleInputChange}
            className="relative h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none"
          >
            <option value="">{assetList ? "All" : "Retrieving..."}</option>
            {assetList &&
              assetList.filter((item: any) => !item.crypto).length > 0 &&
              assetList.filter((item: any) => !item.crypto) && (
                <optgroup label="Fiat">
                  {assetList &&
                    assetList
                      .filter((item: any) => !item.crypto)
                      .map((asset: any) => (
                        <option key={asset.name} value={asset.name}>
                          {asset.name}
                        </option>
                      ))}
                </optgroup>
              )}
            {assetList &&
              assetList.filter((item: any) => item.crypto).length > 0 &&
              assetList.filter((item: any) => item.crypto) && (
                <optgroup label="Crypto">
                  {assetList &&
                    assetList
                      .filter((item: any) => item.crypto)
                      .map((asset: any) => (
                        <option key={asset.name} value={asset.name}>
                          {asset.name}
                        </option>
                      ))}
                </optgroup>
              )}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Merchant
          </label>
          <Tooltip
            title={selectedMerchant ? selectedMerchant.name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !isFilterLoading && setIsMerchantModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isFilterLoading
                  ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                  : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedMerchant ? selectedMerchant.name : "All"}
              </span>
              {selectedMerchant && !isFilterLoading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Subaccount
          </label>
          <Tooltip
            title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !isFilterLoading && selectedMerchant && setIsSubaccountModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isFilterLoading || !selectedMerchant
                  ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                  : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
              </span>
              {selectedSubaccount && !isFilterLoading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        <div className="col-span-2 mt-4 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            isDisabled={isFilterLoading}
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            Clear
          </Button>
          <Button
            type="button"
            isDisabled={isFilterLoading}
            onClick={handleSearchSubmit}
            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="z-9 relative tracking-tight">Search Trades</span>
            <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
          </Button>
        </div>
      </div>

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && selectedMerchant && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant.id}
        />
      )}
    </Box>
  );
};

export default TradeSearch;
